import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DollarSign, Package, ShoppingBag, Star, TrendingUp, Clock, Truck,
  CheckCircle, XCircle, BarChart3, PieChart, ArrowUpRight, ArrowDownRight,
  RefreshCw, Calendar, Tag, Users
} from "lucide-react";
import { getSellerDashboard, getSellerEarnings, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

function StatCard({ title, value, subtitle, icon: Icon, trend, trendUp, color = "text-[var(--liturgical-primary)]" }) {
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-[#6B665E] font-['Nunito'] uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-['Crimson_Text'] font-bold ${color} mt-1`}>{value}</p>
            {subtitle && <p className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5">{subtitle}</p>}
          </div>
          <div className={`w-10 h-10 rounded-lg ${color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${color}`} />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-3 text-xs font-['Nunito'] ${trendUp ? 'text-[var(--liturgical-primary)]' : 'text-[#991B1B]'}`}>
            {trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trend}% from last month
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RevenueChart({ data }) {
  if (!data || data.length === 0) return null;
  
  const maxRevenue = Math.max(...data.map(d => d.revenue), 1);
  
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid="revenue-chart">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-[var(--liturgical-primary)]" />
          Revenue Trend
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-2 h-40">
          {data.map((item, idx) => (
            <div key={idx} className="flex-1 flex flex-col items-center gap-2">
              <div className="w-full bg-[#F0EBE0] rounded-t relative" style={{ height: '120px' }}>
                <div
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[var(--liturgical-primary)] to-[var(--liturgical-primary-light)] rounded-t transition-all duration-500"
                  style={{ height: `${(item.revenue / maxRevenue) * 100}%` }}
                />
              </div>
              <div className="text-center">
                <p className="text-xs font-['Nunito'] font-semibold text-[#2D2A26]">{item.month}</p>
                <p className="text-[10px] text-[#6B665E] font-['Nunito']">${item.revenue.toFixed(0)}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function OrderStatusPie({ data }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return null;
  
  const statusConfig = {
    pending: { color: "bg-amber-500", label: "Pending" },
    confirmed: { color: "bg-blue-500", label: "Confirmed" },
    shipped: { color: "bg-purple-500", label: "Shipped" },
    delivered: { color: "bg-green-500", label: "Delivered" },
    cancelled: { color: "bg-red-500", label: "Cancelled" }
  };
  
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid="order-status-chart">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
          <PieChart className="w-5 h-5 text-[var(--liturgical-primary)]" />
          Order Status
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {Object.entries(data).map(([status, count]) => {
            if (count === 0) return null;
            const config = statusConfig[status];
            const percentage = (count / total) * 100;
            return (
              <div key={status} className="space-y-1">
                <div className="flex items-center justify-between text-xs font-['Nunito']">
                  <span className="text-[#2D2A26]">{config?.label || status}</span>
                  <span className="text-[#6B665E]">{count} ({percentage.toFixed(0)}%)</span>
                </div>
                <div className="h-2 bg-[#F0EBE0] rounded-full overflow-hidden">
                  <div
                    className={`h-full ${config?.color || 'bg-gray-500'} transition-all duration-500`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function RatingOverview({ avgRating, reviewCount, distribution }) {
  const total = Object.values(distribution).reduce((a, b) => a + b, 0);
  
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid="rating-overview">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
          <Star className="w-5 h-5 text-[var(--liturgical-secondary)]" />
          Rating Overview
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-6">
          <div className="text-center">
            <p className="text-4xl font-['Crimson_Text'] font-bold text-[var(--liturgical-secondary)]">{avgRating.toFixed(1)}</p>
            <div className="flex items-center justify-center gap-0.5 mt-1">
              {[1, 2, 3, 4, 5].map(star => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${star <= Math.round(avgRating) ? 'text-[var(--liturgical-secondary)] fill-[#D4AF37]' : 'text-[#E5E0D5]'}`}
                />
              ))}
            </div>
            <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">{reviewCount} reviews</p>
          </div>
          <div className="flex-1 space-y-1.5">
            {[5, 4, 3, 2, 1].map(star => {
              const count = distribution[star] || 0;
              const percentage = total > 0 ? (count / total) * 100 : 0;
              return (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-xs text-[#6B665E] font-['Nunito'] w-3">{star}</span>
                  <Star className="w-3 h-3 text-[var(--liturgical-secondary)] fill-[#D4AF37]" />
                  <div className="flex-1 h-2 bg-[#F0EBE0] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--liturgical-secondary)] transition-all duration-500"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-xs text-[#6B665E] font-['Nunito'] w-6">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function RecentOrdersTable({ orders }) {
  const navigate = useNavigate();
  
  const statusConfig = {
    pending: { color: "bg-amber-100 text-amber-700", icon: Clock },
    confirmed: { color: "bg-blue-100 text-blue-700", icon: CheckCircle },
    shipped: { color: "bg-purple-100 text-purple-700", icon: Truck },
    delivered: { color: "bg-green-100 text-green-700", icon: CheckCircle },
    cancelled: { color: "bg-red-100 text-red-700", icon: XCircle }
  };
  
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid="recent-orders">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
          <Package className="w-5 h-5 text-[var(--liturgical-primary)]" />
          Recent Orders
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/orders?tab=sales')}
          className="text-xs text-[var(--liturgical-primary)] font-['Nunito']"
        >
          View All
        </Button>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <div className="text-center py-8">
            <Package className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
            <p className="text-sm text-[#6B665E] font-['Nunito']">No recent orders</p>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const config = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = config.icon;
              return (
                <div
                  key={order.order_id}
                  className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg cursor-pointer hover:bg-[#F0EBE0] transition-colors"
                  onClick={() => navigate('/orders?tab=sales')}
                  data-testid={`recent-order-${order.order_id}`}
                >
                  <div className="w-10 h-10 rounded overflow-hidden bg-white flex-shrink-0">
                    {order.listing?.image_url ? (
                      <img src={resolveImageUrl(order.listing.image_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-5 h-5 text-[#6B665E]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26] truncate">
                      {order.listing?.title || "Item"}
                    </p>
                    <p className="text-xs text-[#6B665E] font-['Nunito']">
                      {order.buyer?.name} • {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-['Nunito'] font-bold text-[var(--liturgical-primary)]">${order.amount?.toFixed(2)}</p>
                    <Badge className={`${config.color} text-[10px] font-['Nunito']`}>
                      <StatusIcon className="w-2.5 h-2.5 mr-1" />
                      {order.status}
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TopItemsTable({ items }) {
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid="top-items">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-[var(--liturgical-primary)]" />
          Top Selling Items
        </CardTitle>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="text-center py-8">
            <ShoppingBag className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
            <p className="text-sm text-[#6B665E] font-['Nunito']">No sales data yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((item, idx) => (
              <div
                key={item.listing?.listing_id || idx}
                className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg"
                data-testid={`top-item-${idx}`}
              >
                <div className="w-8 h-8 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center text-[var(--liturgical-primary)] font-['Nunito'] font-bold text-sm flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="w-10 h-10 rounded overflow-hidden bg-white flex-shrink-0">
                  {item.listing?.image_url ? (
                    <img src={resolveImageUrl(item.listing.image_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Package className="w-5 h-5 text-[#6B665E]" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-['Nunito'] font-semibold text-[#2D2A26] truncate">
                    {item.listing?.title || "Item"}
                  </p>
                  <p className="text-xs text-[#6B665E] font-['Nunito']">{item.count} sold</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-['Nunito'] font-bold text-[var(--liturgical-primary)]">${item.revenue?.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EarningsBreakdown({ earnings }) {
  if (!earnings) return null;
  
  const categories = Object.entries(earnings.by_category || {}).sort((a, b) => b[1] - a[1]);
  const total = categories.reduce((sum, [, val]) => sum + val, 0);
  
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid="earnings-breakdown">
      <CardHeader className="pb-2">
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26] flex items-center gap-2">
          <Tag className="w-5 h-5 text-[var(--liturgical-primary)]" />
          Earnings by Category
        </CardTitle>
      </CardHeader>
      <CardContent>
        {categories.length === 0 ? (
          <div className="text-center py-8">
            <Tag className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
            <p className="text-sm text-[#6B665E] font-['Nunito']">No earnings data</p>
          </div>
        ) : (
          <div className="space-y-3">
            {categories.slice(0, 5).map(([category, amount]) => {
              const percentage = total > 0 ? (amount / total) * 100 : 0;
              return (
                <div key={category} className="space-y-1">
                  <div className="flex items-center justify-between text-xs font-['Nunito']">
                    <span className="text-[#2D2A26]">{category}</span>
                    <span className="text-[var(--liturgical-primary)] font-semibold">${amount.toFixed(2)}</span>
                  </div>
                  <Progress value={percentage} className="h-2" />
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function SellerDashboardPage({ user }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [earnings, setEarnings] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, earnRes] = await Promise.all([
        getSellerDashboard(),
        getSellerEarnings()
      ]);
      setDashboard(dashRes.data);
      setEarnings(earnRes.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  const summary = dashboard?.summary || {};

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="seller-dashboard-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]" data-testid="dashboard-title">
              Seller Dashboard
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito'] mt-1">
              Track your sales, earnings, and performance
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={loadData}
              className="border-[#E5E0D5] text-[#2D2A26] font-['Nunito']"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              onClick={() => navigate('/marketplace')}
              className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
              data-testid="new-listing-btn"
            >
              <ShoppingBag className="w-4 h-4 mr-2" /> New Listing
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Total Revenue"
            value={`$${summary.total_revenue?.toFixed(2) || '0.00'}`}
            subtitle={`${summary.total_sales || 0} orders`}
            icon={DollarSign}
            color="text-[var(--liturgical-primary)]"
          />
          <StatCard
            title="Active Listings"
            value={summary.active_listings || 0}
            subtitle={`${summary.sold_listings || 0} sold`}
            icon={ShoppingBag}
            color="text-[var(--liturgical-primary)]"
          />
          <StatCard
            title="Seller Rating"
            value={summary.avg_rating?.toFixed(1) || '0.0'}
            subtitle={`${summary.review_count || 0} reviews`}
            icon={Star}
            color="text-[var(--liturgical-secondary)]"
          />
          <StatCard
            title="Pending Actions"
            value={summary.pending_orders || 0}
            subtitle={`${summary.orders_to_ship || 0} to ship`}
            icon={Clock}
            color="text-[var(--liturgical-primary)]"
          />
        </div>

        {/* Earnings Summary */}
        {earnings && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card className="bg-gradient-to-br from-[#3E7B40] to-[#2D5A2E] text-white">
              <CardContent className="p-5">
                <p className="text-xs font-['Nunito'] opacity-80">Total Earned</p>
                <p className="text-3xl font-['Crimson_Text'] font-bold mt-1">${earnings.total_earned?.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] text-white">
              <CardContent className="p-5">
                <p className="text-xs font-['Nunito'] opacity-80">Pending Payout</p>
                <p className="text-3xl font-['Crimson_Text'] font-bold mt-1">${earnings.pending_payout?.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-[#D4AF37] to-[#B8942D] text-white">
              <CardContent className="p-5">
                <p className="text-xs font-['Nunito'] opacity-80">Completed</p>
                <p className="text-3xl font-['Crimson_Text'] font-bold mt-1">${earnings.completed_earnings?.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <RevenueChart data={dashboard?.revenue_trend} />
          <OrderStatusPie data={dashboard?.order_status || {}} />
        </div>

        {/* Rating and Earnings Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <RatingOverview
            avgRating={summary.avg_rating || 0}
            reviewCount={summary.review_count || 0}
            distribution={dashboard?.rating_distribution || {}}
          />
          <EarningsBreakdown earnings={earnings} />
        </div>

        {/* Tables Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RecentOrdersTable orders={dashboard?.recent_orders || []} />
          <TopItemsTable items={dashboard?.top_items || []} />
        </div>
      </div>
    </div>
  );
}
